/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LockedOverlay from 'components/atoms/locked_overlay';
import useOnlyConlicitaseg from 'hooks/useOnlyConlicitaseg';
import { disconnectConsumer } from 'services/cable';
import { isConlicitasegAllowedRoute } from 'services/conlicitaseg_only_routes';
import { getUserInfo } from 'services/actions/users_actions';
import { ApplicationState, persistor } from 'store';
import { MenusBehaviourTypes } from 'store/modules/menus_behaviour_store/types';
import { profileSignOut } from 'store/modules/profile_store/actions';
import { ProfileState, ProfileTypes } from 'store/modules/profile_store/types';

interface HeaderUserMenuProps {
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Avatar e menu da conta, comum ao topo do Conlicitação e ao do ConlicitaSeg.
 * O estilo de `user-avatar`/`user-dropdown` fica no Nav de cada header.
 */
const HeaderUserMenu = ({ isOpen, onToggle }: HeaderUserMenuProps) => {
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isOnlyConlicitaseg = useOnlyConlicitaseg();
  const isLockedRoute = (route: string) => isOnlyConlicitaseg && !isConlicitasegAllowedRoute(route);

  const avatarUrl = profile?.avatar_url;

  useEffect(() => {
    if (!profile?.id || profile.avatar_url !== undefined) return;
    getUserInfo(profile.id)
      .then((response) => {
        const url = response?.data?.user?.avatar_url || null;
        dispatch({ type: ProfileTypes.SET_AVATAR_URL, avatar_url: url });
      })
      .catch(() => {
        dispatch({ type: ProfileTypes.SET_AVATAR_URL, avatar_url: null });
      });
  }, [profile?.id]);

  const handleSignOut = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    try {
      const response: any = await dispatch(profileSignOut());
      if (response?.status >= 400) return;
    } catch {
      return;
    }

    disconnectConsumer();
    dispatch({ type: 'CLEAR_REDUX' });
    await persistor.flush();
    // recarga completa: aborta requisições em voo, que poderiam regravar o cookie da sessão encerrada
    window.location.href = '/';
  };

  return (
    <li className="nav-item dropdown position-relative">
      <a
        className="nav-link nav-link-menu px-3 py-0 dropdown-toggle user-dropdown"
        role="button"
        href="#"
        onClick={onToggle}
      >
        <div className="user-avatar rounded-circle overflow-hidden" title={profile?.name || ''}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={profile?.name || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => dispatch({ type: ProfileTypes.SET_AVATAR_URL, avatar_url: null })}
            />
          ) : (
            profile.name
              ?.split(' ')
              .map((n) => n[0])
              .slice(0, 2)
              .join('')
              .toUpperCase()
          )}
        </div>
      </a>
      <ul className={`dropdown-menu dropdown-menu-right ${isOpen && 'show'}`}>
        {profile && !profile.in_analysis && (
          <>
            <li className="nav-item">
              {isLockedRoute('/minha_conta') ? (
                <LockedOverlay variant="inline" className="d-block">
                  <span className="dropdown-item">Minha Conta</span>
                </LockedOverlay>
              ) : (
                <a className="dropdown-item" target="_top" href="/minha_conta">
                  Minha Conta
                </a>
              )}
            </li>
            <li className="dropdown-divider" />
            <li className="nav-item">
              <a className="dropdown-item" target="_top" href="/planos">
                Planos e Preços
              </a>
            </li>
          </>
        )}
        <li className="dropdown-divider" />
        <li className="nav-item">
          <a
            className="dropdown-item"
            target="#feedback_bootstrap_modal"
            data-toggle="modal"
            href=""
            onClick={() =>
              dispatch({ type: MenusBehaviourTypes.OPEN_FEEDBACK_MODAL, pathname: '' })
            }
          >
            Envie seu Feedback
          </a>
        </li>
        <li className="dropdown-divider" />
        <li className="nav-item">
          <a
            className="dropdown-item"
            target="#feedback_bootstrap_modal"
            data-toggle="modal"
            href=""
            onClick={handleSignOut}
          >
            Sair
          </a>
        </li>
      </ul>
    </li>
  );
};

export default HeaderUserMenu;
